import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import PowerSelectMultiple from 'ember-power-select/components/power-select-multiple';
import { not, or } from 'ember-truth-helpers';
import MessageBox from 'garaje/components/ui/classic/message-box';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import UiButton from 'garaje/components/ui-button';
import doubleConfirmation from 'garaje/helpers/double-confirmation';
import type BusinessHoursManager from 'garaje/utils/presence/business-hours-manager';

// "Outside business hours presence report", rendered inside the Location settings page's
// "Security" section. A collapsed SettingsPanel by default — "Edit" when the report is on, "Enable"
// when it isn't (Enable replaces the toggle: it expands the panel and turns the report on, unsaved
// until Save). Enable is disabled until business hours is configured, since the report can't fire
// without a window. State + persistence live on the shared BusinessHoursManager.
interface LocationSettingsAfterHoursReportSignature {
  Args: {
    manager: BusinessHoursManager;
  };
}

export default class LocationSettingsAfterHoursReport extends Component<LocationSettingsAfterHoursReportSignature> {
  @tracked isOpen = false;

  @action
  open(): void {
    this.isOpen = true;
  }

  @action
  close(): void {
    this.isOpen = false;
  }

  // Enable replaces the toggle: expand the panel and turn the report on (unsaved until Save).
  @action
  enable(): void {
    this.args.manager.toggleAfterHoursReport(true);
    this.isOpen = true;
  }

  @action
  rollback(): void {
    this.args.manager.rollbackAfterHoursReport();
  }

  @action
  async save(): Promise<void> {
    const saved = await this.args.manager.saveAfterHoursReport();
    if (saved) this.close();
  }

  // Turn the report off and persist immediately.
  @action
  async disable(): Promise<void> {
    this.args.manager.toggleAfterHoursReport(false);
    const saved = await this.args.manager.saveAfterHoursReport();
    if (saved) this.close();
  }

  // "Set now": do the same as the Business hours panel's Enable button (turn it on + expand it), then
  // scroll up to it. The panel's top doesn't move as it expands (content grows downward), so a
  // synchronous scroll lands correctly. The id matches the panel in components/location-settings/business-hours.
  @action
  configureBusinessHours(): void {
    this.args.manager.startConfiguringBusinessHours();
    document.getElementById('business-hours')?.scrollIntoView({ behavior: 'smooth' });
  }

  <template>
    <SettingsPanel data-test-after-hours-report-panel @isOpen={{this.isOpen}} as |panel|>
      <panel.header
        @iconUri="/assets/images/features/survey.svg"
        @title="Outside business hours presence report"
        @onCancel={{doubleConfirmation
          close=this.close
          rollback=this.rollback
          dirty=(or @manager.reportOptInDirty @manager.afterHoursRecipientsDirty)
        }}
        as |SPHeader|
      >
        <SPHeader.description>
          Receive a daily report of people still on-site outside of this location's business hours.
          <a href="https://envoy.help/en/articles/12159459-presence-control-center" target="_blank" rel="noopener">
            Learn more
          </a>
        </SPHeader.description>
        <SPHeader.controls>
          {{#if this.isOpen}}
            {{#if @manager.reportConfigured}}
              <UiButton {{on "click" this.disable}} class="btn btn--small btn--outlineFill" data-test-disable-button>
                Disable
              </UiButton>
            {{/if}}
            <UiButton
              {{on "click" this.save}}
              class="btn btn--small btn--brand"
              disabled={{@manager.afterHoursReportSaveDisabled}}
              data-test-save-button
            >
              Save
            </UiButton>
          {{else if @manager.reportConfigured}}
            <UiButton {{on "click" this.open}} class="btn btn--small btn--brand" data-test-edit-button>
              Edit
            </UiButton>
          {{else}}
            <UiButton
              {{on "click" this.enable}}
              class="btn btn--small btn--brand"
              disabled={{not @manager.businessHoursConfigured}}
              data-test-enable-button
            >
              Enable
            </UiButton>
          {{/if}}
        </SPHeader.controls>
      </panel.header>

      {{! Disabling the Enable button alone isn't obvious enough — call out the unmet dependency inside
          the card, below the description. The "warning" type renders the yellow (papaya) styling.
          Clears live once business hours is configured (shared manager). }}
      {{#unless @manager.businessHoursConfigured}}
        <MessageBox @type="warning" class="mt-4" data-test-after-hours-report-disabled-notice>
          <p class="mb-0">
            This location's business hours have not been set.
            <button
              type="button"
              class="btn btn--link !text-gem-50 !p-0 align-baseline !text-sm !font-normal !cursor-pointer"
              {{on "click" this.configureBusinessHours}}
              data-test-set-business-hours-link
            >
              Set now &rarr;
            </button>
          </p>
        </MessageBox>
      {{/unless}}

      <panel.body as |SPContent|>
        <SPContent class="mb-4">
          <div class="mt-4" data-test-after-hours-report-recipients>
            <label class="block text-sm font-medium text-carbon-70 mb-2">Recipients</label>
            <PowerSelectMultiple
              @options={{@manager.afterHoursAvailableRecipients}}
              @selected={{@manager.afterHoursSelectedRecipients}}
              @onChange={{@manager.updateAfterHoursRecipients}}
              @searchEnabled={{true}}
              @searchField="name"
              @placeholder="Select users"
              as |user|
            >
              {{user.name}}
            </PowerSelectMultiple>
            <p class="text-sm text-gray-600 mt-1">
              If no one is selected, the report goes to location admins at this location.
            </p>
          </div>
        </SPContent>
      </panel.body>
    </SettingsPanel>
  </template>
}
